import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { inputClass } from '@/components/ui';
import { adminProductCategoriesQuery } from '@/queries';

/** Not a category anybody would type, and never saved: the select's own value. */
const NEW = '\u0000new';

/**
 * The category of a style: pick one in use, or add one of your own.
 *
 * Tracker #12. Guy asked whether Category was linked to anything, and then:
 * "Can you make it so we can add the category's ourselves in a Field are
 * option. What ever I tell you today will be wrong in a month."
 *
 * He is right about the month, so nothing here names a category. The choices
 * are the categories his own styles carry, read back from the catalogue. A new
 * one is made by typing it, and every form opened after that offers it.
 *
 * Two screens set a category, the style page and the new product form, and
 * each had a bare text box. Two bare text boxes is how "Tees", "tees" and
 * "Tee's" become three categories that no filter can put back together.
 *
 * The style's own category is always one of the choices, even before the list
 * arrives and even if it is the only style that carries it. A select that
 * cannot show the value it was given shows the wrong one.
 *
 * **It falls back to the text box.** If the list cannot be read, this is a
 * plain input and the form still saves. A control that will not render because
 * a second request failed is a worse fault than the one it is reporting.
 */
export function CategoryField(
  { id, value, onChange }: { id: string; value: string; onChange: (v: string) => void },
) {
  const list = useQuery({ ...adminProductCategoriesQuery, retry: false });
  const known = list.data?.categories ?? [];
  const [typing, setTyping] = useState(false);
  const choices = value.trim() !== '' && !known.includes(value) ? [value, ...known] : known;

  if (list.isError) {
    return (
      <input
        id={id} className={inputClass} value={value} autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <>
      <select
        id={id}
        className={inputClass}
        value={typing ? NEW : value}
        onChange={(e) => {
          if (e.target.value === NEW) {
            setTyping(true);
            onChange('');
            return;
          }
          setTyping(false);
          onChange(e.target.value);
        }}
      >
        <option value="">Choose a category</option>
        {choices.map((c) => <option key={c} value={c}>{c}</option>)}
        <option value={NEW}>Add a new category</option>
      </select>
      {typing && (
        <input
          // Named, because the label points at the select above it, and a
          // field with no name of its own is one a screen reader cannot say.
          aria-label="New category"
          className={`${inputClass} mt-2`}
          value={value}
          placeholder="Type the new category"
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </>
  );
}
